// ============================================
// CoinModal — displays detailed coin information
// ============================================
import { useEffect, useState } from 'react';
import Modal from '../Modal/Modal';
import ShareButtons from '../ShareButtons/ShareButtons';
import AppPromotionCard from '../AppPromotionCard/AppPromotionCard';
import CoinMedia, { SignatureBadge } from '../CoinMedia/CoinMedia';
import styles from './CoinModal.module.css';

const rarityConfig = {
    gold: {
        color: '#d4af37',
        glow: 'rgba(212, 175, 55, 0.5)',
        label: 'Gold',
    },
    silver: {
        color: '#c0c0c0',
        glow: 'rgba(192, 192, 192, 0.5)',
        label: 'Silver',
    },
    bronze: {
        color: '#cd7f32',
        glow: 'rgba(205, 127, 50, 0.5)',
        label: 'Bronze',
    },
    legendary: {
        color: '#9d4edd',
        glow: 'rgba(157, 78, 221, 0.5)',
        label: 'Legendary',
    },
    collection: {
        color: '#d4af37',
        glow: 'rgba(212, 175, 55, 0.5)',
        label: 'Collection',
    },
};


function CoinModal({ isOpen, onClose, coin, onUpgrade }) {
    const [countdown, setCountdown] = useState('');

    // Derived values — computed before any hooks so they're available in effects
    const rarity = coin?.rarity || coin?.tier || 'bronze';
    const isCollection = coin?.sourceType === 'collection';
    const isMaxTier = rarity === 'legendary';
    const legendaryLocked = rarity === 'gold' && coin?.legendaryAvailableAt
        && new Date(coin.legendaryAvailableAt) > new Date();
    const canUpgrade = !isMaxTier && !!coin?.canRetry && !legendaryLocked;
    const isOnCooldown = !isMaxTier && (!coin?.canRetry || legendaryLocked) && !!(coin?.nextRetryAt || coin?.legendaryAvailableAt);
    const countdownTarget = coin?.nextRetryAt || (rarity === 'gold' ? coin?.legendaryAvailableAt : null);

    useEffect(() => {
        if (coin) {
            console.log('CoinModal - coin data:', {
                rarity: coin.rarity,
                canRetry: coin.canRetry,
                nextRetryAt: coin.nextRetryAt,
                legendaryAvailableAt: coin.legendaryAvailableAt,
                legendaryLocked,
                isOnCooldown,
                canUpgrade,
            });
        }
    }, [coin, legendaryLocked, isOnCooldown, canUpgrade]);

    useEffect(() => {
        if (!countdownTarget) {
            setCountdown('');
            return;
        }

        const updateCountdown = () => {
            const now = new Date();
            const retry = new Date(countdownTarget);
            const diffMs = retry - now;

            if (diffMs <= 0) {
                setCountdown('Ready now!');
                return;
            }

            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

            if (hours > 0) {
                setCountdown(`${hours}h ${minutes}m ${seconds}s`);
            } else if (minutes > 0) {
                setCountdown(`${minutes}m ${seconds}s`);
            } else {
                setCountdown(`${seconds}s`);
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [countdownTarget]);

    if (!coin) return null;

    const config = rarityConfig[rarity] || rarityConfig.bronze;
    // Collection coins always display as purple "Collection" regardless of underlying rarity
    const displayConfig = isCollection
        ? { ...rarityConfig.legendary, label: 'Collection' }
        : config;
    const title = coin.mediaTitle || coin.name || coin.movie || 'Unknown';
    const seasonLabel = coin.seasonNumber ? `Season ${coin.seasonNumber}` : null;
    const quizScore = coin.quizScore || coin.score || null;
    const earnedDate = coin.watchedAt ? new Date(coin.watchedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }) : null;

    const mediaType = coin.mediaType || (coin.seasonNumber ? 'TV Series' : 'Movie');
    const coinShareUrl = coin.id && typeof window !== 'undefined' ? `${window.location.origin}/coin/${coin.shareCode || coin.id}` : null;
    const coinShareTitle = rarity === 'legendary'
        ? `${title} LEGENDARY coin on Aluvo 💎`
        : `${config.label} ${title} coin on Aluvo`;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className={styles.content}>
                {/* Large Coin Display */}
                <div
                    className={styles.coinDisplay}
                    style={{
                        '--coin-color': displayConfig.color,
                        '--coin-glow': displayConfig.glow,
                    }}
                >
                    <div className={styles.coinInner}>
                        {/* Detail view: Signature coins loop here (poster for reduced motion) */}
                        <CoinMedia
                            coin={coin}
                            animate
                            alt={title}
                            className={styles.coinImage}
                            fallback={<span className={styles.emoji}>{coin.emoji || '🪙'}</span>}
                        />
                    </div>
                    <div className={styles.shine} />
                </div>

                {/* Coin Details */}
                <div className={styles.details}>
                    <SignatureBadge coin={coin} />
                    <h2
                        className={styles.title}
                        style={isCollection ? { color: '#a78bfa' } : undefined}
                    >
                        {title}
                    </h2>

                    {seasonLabel && (
                        <p className={styles.season}>{seasonLabel}</p>
                    )}

                    {mediaType && coin.sourceType !== 'collection' && (
                        <p className={styles.mediaType}>{mediaType}</p>
                    )}

                    <div className={styles.infoGrid}>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Rarity</span>
                            <span
                                className={styles.infoValue}
                                style={{ color: displayConfig.color, fontWeight: 700 }}
                            >
                                {displayConfig.label}
                            </span>
                        </div>

                        {quizScore !== null && quizScore !== undefined && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Challenge Score</span>
                                <span className={styles.infoValue}>{quizScore}%</span>
                            </div>
                        )}

                        {earnedDate && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Earned On</span>
                                <span
                                    className={styles.infoValue}
                                    style={{ color: config.color, fontWeight: 700 }}
                                >
                                    {earnedDate}
                                </span>
                            </div>
                        )}
                    </div>

                    {coinShareUrl && (
                        <div className={styles.shareCoinPanel}>
                            <p className={styles.shareCoinText}>Share this verified coin as public proof.</p>
                            <ShareButtons url={coinShareUrl} title={coinShareTitle} label="Share coin:" />
                            <a href={coinShareUrl} className={styles.sharePageLink}>Open coin share page →</a>
                        </div>
                    )}

                    {/* Upgrade Status Section */}
                    {isMaxTier ? (
                        <div className={styles.statusBanner} data-status="max">
                            <span className={styles.statusIcon}>✦</span>
                            <span className={styles.statusText}>You've reached Legendary — the highest tier!</span>
                        </div>
                    ) : isOnCooldown ? (
                        <>
                            <div className={styles.statusBanner} data-status="cooldown">
                                <span className={styles.statusIcon}>⏱️</span>
                                <div className={styles.statusTextBlock}>
                                    <span className={styles.statusText}>
                                        {rarity === 'gold' ? 'Legendary attempt unlocks in:' : 'Upgrade available in:'}
                                    </span>
                                    <span className={styles.countdown}>{countdown}</span>
                                </div>
                            </div>
                            {rarity === 'gold' && (
                                <AppPromotionCard
                                    variant="post_gold"
                                    compact
                                />
                            )}
                        </>
                    ) : canUpgrade ? (
                        <>
                            <div className={styles.statusBanner} data-status="ready">
                                <span className={styles.statusIcon}>✨</span>
                                <span className={styles.statusText}>
                                    {rarity === 'gold'
                                        ? '💎 Ready for Legendary — perfect score required!'
                                        : 'Upgrade available! Take another challenge to earn a higher tier.'}
                                </span>
                            </div>
                            {onUpgrade && (
                                <button className={styles.upgradeBtn} onClick={onUpgrade}>
                                    {rarity === 'gold' ? '💎 Take the Legendary Challenge' : '✨ Try Again to Upgrade'}
                                </button>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
}

export default CoinModal;